import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { DealType } from 'components/common/types/Deal.types';
import { changeDealCurrentLineData } from 'store/dealManagement/reducer';
import { addToastNotification } from 'store/notification/reducer';
import { getAsDate } from 'utils/getAsDate';

import {
  getEndOfToday,
  getStartOfToday,
  isEndDateWithinGuaranteedLimit,
  isGuaranteedDurationExceeded,
  shouldNotifyPatternExtension,
} from './dateSelector.utils';

type UseDateSelectorScheduleParams = {
  dealType: DealType;
  startDate: Date | string | null | undefined;
  endDate: Date | string | null | undefined;
  handlePatternInfoMessage: () => void;
  isPatternInfoMessage: boolean;
};

type UseDateSelectorScheduleResult = {
  disabledEndDates: Date;
  changeEndDate: (date: Date | null) => void;
};

const GUARANTEED_DURATION_WARNING =
  'The maximum duration for a guaranteed line is 6 months. Please revise your end date, or create another line.';

export const useDateSelectorSchedule = ({
  dealType,
  startDate,
  endDate,
  handlePatternInfoMessage,
  isPatternInfoMessage,
}: UseDateSelectorScheduleParams): UseDateSelectorScheduleResult => {
  const dispatch = useDispatch();
  const [disabledEndDates, setDisabledEndDates] = useState(getStartOfToday());

  useEffect(() => {
    if (!startDate) {
      dispatch(changeDealCurrentLineData({ startDate: getStartOfToday() }));
    }

    if (!endDate) {
      dispatch(changeDealCurrentLineData({ endDate: getEndOfToday() }));
    }
  }, []);

  useEffect(() => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      dispatch(changeDealCurrentLineData({ endDate: startDate }));
    }

    if (startDate) {
      setDisabledEndDates(getAsDate(startDate, getStartOfToday()));
    }
  }, [startDate, endDate, dispatch]);

  useEffect(() => {
    if (!isGuaranteedDurationExceeded(dealType, startDate, endDate)) return;

    dispatch(changeDealCurrentLineData({ endDate: startDate }));
  }, [dealType, startDate, endDate, dispatch]);

  const changeEndDate = useCallback(
    (date: Date | null) => {
      if (!date) return;

      if (isEndDateWithinGuaranteedLimit(dealType, startDate, date)) {
        dispatch(changeDealCurrentLineData({ endDate: date }));

        if (shouldNotifyPatternExtension(endDate, date, isPatternInfoMessage)) {
          handlePatternInfoMessage();
        }

        return;
      }

      dispatch(changeDealCurrentLineData({ endDate }));
      dispatch(
        addToastNotification({
          description: GUARANTEED_DURATION_WARNING,
          type: 'Warning',
          timeout: 10000,
        }),
      );
    },
    [dealType, startDate, endDate, isPatternInfoMessage, handlePatternInfoMessage, dispatch],
  );

  return { disabledEndDates, changeEndDate };
};
